import { z } from "zod/v4";
import { defineEndpoint, defineResource } from "@visioni/a3s-client-api";
import { FEATURE_NAME } from "../feature-name";
import { ListTenantActivationsQueryParams } from "./activations";

/**
 * Patriot signals — the events Patriot received and recorded — listed at
 * whichever level of a tenant the caller is looking at.
 *
 * The signal list is the activation list's upstream: a signal appears here
 * whether or not it raised an activation, so comparing the two shows which
 * signals Patriot received but raised nothing for.
 *
 * Only signals carrying an a3s marker can be placed in a tenant, so only those
 * are listed.
 *
 * Same **allowlist** discipline as `activations.ts`: a field reaches a caller
 * only by being named here. Excluded on purpose, beyond what that module
 * already excludes:
 *
 * - `signalId` — Patriot's internal integer. The id a caller can do something
 *   with is the a3s one from the marker, which names the camera and the clip.
 * - `notes` — the raw free-text field the marker lives in. The marker is
 *   already surfaced, parsed, as `a3sSignalId`.
 * - `zoneNumber` / `userNumber` / `moduleNo` / `callerIdNo` / `latitude` /
 *   `longitude` / `currentStatus` / `previousStatus` / `alarmType` — Patriot
 *   addressing and device-state plumbing.
 */

/**
 * One row of a signal list.
 *
 * Every row carries an a3s marker — the list reads the a3s axes, which hold
 * nothing else — so the a3s fields are required here, where on an activation
 * they are not.
 */
export const SignalSummary = z.object({
  /** The a3s `SignalId` from the marker: the camera and the instant of the clip it carried. */
  a3sSignalId: z.string(),
  /** When Patriot received the signal, normalised to UTC. */
  receivedDateTime: z.string(),
  /** Patriot's description line, e.g. `Vehicle Detected`. */
  description: z.string().optional(),
  /**
   * The activation this signal raised or joined. Absent when it raised none,
   * which is always the case for a test signal.
   */
  ticketNo: z.string().optional(),
  a3sSite: z.string(),
  a3sAsset: z.string(),
  a3sCamera: z.string(),
});
export type SignalSummary = z.output<typeof SignalSummary>;

export const ListTenantSignalsResponse = z.object({
  items: z.array(SignalSummary),
  /** Opaque resume token; `null` when the list is exhausted. Pass back verbatim. */
  cursor: z.string().nullable(),
});
export type ListTenantSignalsResponse = z.output<typeof ListTenantSignalsResponse>;

/**
 * The activation list's filters, unchanged. The signal axes mirror the
 * activation ones partition for partition, so the same combinations are
 * servable and the same one — a camera with no site — is refused. `since` /
 * `until` bound the signal's `receivedDateTime`.
 */
export const ListTenantSignalsQueryParams = ListTenantActivationsQueryParams;
export type ListTenantSignalsQueryParamsWire = z.input<typeof ListTenantSignalsQueryParams>;
export type ListTenantSignalsQueryParams = z.output<typeof ListTenantSignalsQueryParams>;

/**
 * ```
 * /features/monitoring/patriot/tenant/signals[?site=&camera=&since=&until=]
 * ```
 *
 * Beside `patriot/tenant/activations`, for the reasons given on
 * `tenantActivationsResource`. There is no per-signal detail route: a row
 * carries everything the allowlist exposes, and its activation opens on the
 * activation route by `ticketNo`.
 */
export const tenantSignalsResource = defineResource({
  feature: FEATURE_NAME,
  resource: "patriot/tenant/signals",
  scope: "tenant",
});

export const listTenantSignalsEndpoint = defineEndpoint(tenantSignalsResource, {
  responseSchema: ListTenantSignalsResponse,
  querySchema: ListTenantSignalsQueryParams,
});
